"use client";

import { useEffect, useMemo, useRef } from "react";
import {
  MobileModalDebugEvent,
  type PersistentDebugLogger,
  captureMobileModalEnvironment,
} from "@/hooks/telemetry";
import type { HomePageModalDebugState } from "./domain/home-page/types";

interface UseHomePageModalDebugStateInput {
  authLoading: boolean;
  hasOpenedTeamDetailsModal: boolean;
  hasOpenedTeamsModal: boolean;
  isTeamGalleryCoveredByDetails: boolean;
  modalDebugLogger: PersistentDebugLogger;
  showAuthModal: boolean;
  showPricingModal: boolean;
  showTeamDetailsModal: boolean;
  showTeamsModal: boolean;
  teamDetailsTeamId: string | null;
  userId: string | null;
  viewport: string | null;
  viewportLoaded: boolean;
}

export function useHomePageModalDebugState(
  input: UseHomePageModalDebugStateInput,
): HomePageModalDebugState {
  const {
    authLoading,
    hasOpenedTeamDetailsModal,
    hasOpenedTeamsModal,
    isTeamGalleryCoveredByDetails,
    modalDebugLogger,
    showAuthModal,
    showPricingModal,
    showTeamDetailsModal,
    showTeamsModal,
    teamDetailsTeamId,
    userId,
    viewport,
    viewportLoaded,
  } = input;

  const modalDebugState = useMemo<HomePageModalDebugState>(() => ({
    showAuthModal,
    showPricingModal,
    showTeamsModal,
    showTeamDetailsModal,
    isTeamGalleryCoveredByDetails,
    hasOpenedTeamsModal,
    hasOpenedTeamDetailsModal,
    teamDetailsTeamId,
    viewport,
    viewportLoaded,
    userId,
    authLoading,
  }), [
    authLoading,
    hasOpenedTeamDetailsModal,
    hasOpenedTeamsModal,
    isTeamGalleryCoveredByDetails,
    showAuthModal,
    showPricingModal,
    showTeamDetailsModal,
    showTeamsModal,
    teamDetailsTeamId,
    userId,
    viewport,
    viewportLoaded,
  ]);
  const previousModalDebugStateRef =
    useRef<HomePageModalDebugState | null>(null);

  useEffect(() => {
    const previousState = previousModalDebugStateRef.current;
    const payload = {
      environment: captureMobileModalEnvironment(),
    };

    if (previousState === null) {
      modalDebugLogger.logMarker({
        name: MobileModalDebugEvent.HomepageModalStateInitialized,
        payload: {
          state: modalDebugState,
          ...payload,
        },
      });
      previousModalDebugStateRef.current = modalDebugState;
      return;
    }

    modalDebugLogger.logStateTransition({
      name: MobileModalDebugEvent.HomepageModalStateChanged,
      from: previousState,
      to: modalDebugState,
      payload,
    });
    previousModalDebugStateRef.current = modalDebugState;
  }, [modalDebugLogger, modalDebugState]);

  return modalDebugState;
}
